import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import SSlider from '@/components/ui/molecules/SSlider';
import SBack from '@/components/ui/molecules/SBack';
import SLoader from '@/components/ui/atoms/SLoader';
import { useToast } from '@/context/ToastContext';
import lockImage from '../../../assets/lock.png';
import lockOpeningImage from '../../../assets/lock-opening.png';
import unlockImage from '../../../assets/unlock.png';
import { accessControlService, OpenDoorRequest } from '@/services/access-control.service';
import './OpenGate.css';

const SUPPORT_PHONE = '+13127445028';
const SUPPORT_DISPLAY = '+1 (312) 744-5028';

// Delay after a successful "Gate" open before auto-advancing to the
// facility tab, so the user gets to see the success state settle first.
const AUTO_ADVANCE_DELAY_MS = 2200;

type DoorTab = 'gate' | 'facility';

const OpenGate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [isGateOpened, setIsGateOpened] = useState(false);

  const [isFacilityUnlocked, setIsFacilityUnlocked] = useState(false);
  const [isFacilityOpening, setIsFacilityOpening] = useState(false);
  const [isFacilityOpened, setIsFacilityOpened] = useState(false);

  const [activeTab, setActiveTab] = useState<DoorTab>('gate');

  // bookingId from nav state (real flow) or URL query param (direct testing)
  const bookingId: string | null =
    location.state?.bookingId ?? searchParams.get('bookingId');
  const facilityName: string | null =
    location.state?.facilityName ?? searchParams.get('facilityName');
  // Booking's own FacilityType (e.g. RECOVERY_ROOM) — sent to the API so the
  // second slider can open a facility door that the default open-door call
  // deliberately excludes (see DEFAULT_EXCLUDED_FACILITY_TYPES server-side).
  const facilityType: string | null =
    location.state?.facilityType ?? searchParams.get('facilityType');

  // Per-(location, facility) flag (BookingType.accessControlEnabled) — fetched
  // lazily here, only once the member actually reaches this screen for a
  // specific booking. Deliberately NOT resolved when listing bookings (that
  // would mean a BookingType lookup per booking on every dashboard/list load,
  // including bookings days out that the member may never check in for).
  const [accessControlEnabled, setAccessControlEnabled] = useState(false);
  // True until the facility-status check resolves — the whole screen shows
  // the standard Tee Box fill loader during this window (same as the booking
  // flow) so the second slider never just pops in mid-screen.
  const [isCheckingAccessControl, setIsCheckingAccessControl] = useState(!!bookingId);

  useEffect(() => {
    if (!bookingId) {
      setIsCheckingAccessControl(false);
      return;
    }
    let cancelled = false;
    accessControlService
      .getFacilityAccessStatus(bookingId)
      .then((status) => {
        if (!cancelled) setAccessControlEnabled(status.accessControlEnabled);
      })
      .catch(() => {
        // Fail closed — just don't show the second slider if the check fails.
      })
      .finally(() => {
        if (!cancelled) setIsCheckingAccessControl(false);
      });
    return () => { cancelled = true; };
  }, [bookingId]);

  // Whether a second "open facility" door should be offered at all for this booking.
  const showFacilitySlider = accessControlEnabled && !!facilityType;
  const facilityLabel = facilityName ?? 'Facility';

  const reset = () => { setIsOpening(false); setIsUnlocked(false); };
  const resetFacility = () => { setIsFacilityOpening(false); setIsFacilityUnlocked(false); };

  const showTechnicalDifficultyToast = () => {
    showToast({
      variant: 'error',
      title: 'Technical Difficulty',
      message: 'There seems to be a technical problem opening the door, please contact Tee Box',
      ctaText: SUPPORT_DISPLAY,
    });
  };

  const handleUnlock = async () => {
    setIsUnlocked(true);
    setIsOpening(true);

    if (!bookingId) {
      showTechnicalDifficultyToast();
      reset();
      return;
    }

    try {
      const req: OpenDoorRequest = { bookingId };
      const response = await accessControlService.openDoor(req);

      if (response.opened) {
        setTimeout(() => {
          setIsOpening(false);
          setIsGateOpened(true);
        }, 2000);
      } else {
        showTechnicalDifficultyToast();
        reset();
      }
    } catch {
      showTechnicalDifficultyToast();
      reset();
    }
  };

  const handleFacilityUnlock = async () => {
    setIsFacilityUnlocked(true);
    setIsFacilityOpening(true);

    if (!bookingId || !facilityType) {
      showTechnicalDifficultyToast();
      resetFacility();
      return;
    }

    try {
      const req: OpenDoorRequest = { bookingId, facilityType };
      const response = await accessControlService.openDoor(req);

      if (response.opened) {
        setTimeout(() => {
          setIsFacilityOpening(false);
          setIsFacilityOpened(true);
        }, 2000);
      } else {
        showTechnicalDifficultyToast();
        resetFacility();
      }
    } catch {
      showTechnicalDifficultyToast();
      resetFacility();
    }
  };

  // Redirect home once every door required for this screen has been opened —
  // full reload (not SPA navigate) so Home re-fetches bookings fresh, since
  // the door-open call updates check-in status server-side and this
  // guarantees the dashboard doesn't serve a stale cached ACTIVE booking.
  useEffect(() => {
    const allOpened = isGateOpened && (!showFacilitySlider || isFacilityOpened);
    if (!allOpened) return;
    const timer = setTimeout(() => { window.location.href = '/home'; }, 3000);
    return () => clearTimeout(timer);
  }, [isGateOpened, isFacilityOpened, showFacilitySlider]);

  // Once the gate is opened, guide the user to the facility tab automatically
  // instead of making them notice and tap over themselves.
  useEffect(() => {
    if (!showFacilitySlider || !isGateOpened || isFacilityOpened || activeTab !== 'gate') return;
    const timer = setTimeout(() => setActiveTab('facility'), AUTO_ADVANCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [showFacilitySlider, isGateOpened, isFacilityOpened, activeTab]);

  const getLockImage = () => {
    if (isGateOpened) return unlockImage;
    if (isOpening) return lockOpeningImage;
    return lockImage;
  };

  const getStatusText = () => {
    if (isGateOpened) return 'Door Opened!';
    if (isOpening) return 'Opening…';
    if (showFacilitySlider) return 'Open Door';
    return facilityName ? `Open ${facilityName}` : 'Open Door';
  };

  const getStatusSub = () => {
    if (isGateOpened) return 'Enjoy your session';
    if (isOpening) return 'Please wait…';
    return 'Slide to unlock the door';
  };

  const getFacilityLockImage = () => {
    if (isFacilityOpened) return unlockImage;
    if (isFacilityOpening) return lockOpeningImage;
    return lockImage;
  };

  const getFacilityStatusText = () => {
    if (isFacilityOpened) return `${facilityLabel} Opened!`;
    if (isFacilityOpening) return 'Opening…';
    return `Open ${facilityLabel}`;
  };

  const getFacilityStatusSub = () => {
    if (isFacilityOpened) return 'Enjoy your session';
    if (isFacilityOpening) return 'Please wait…';
    return `Slide to unlock ${facilityLabel}`;
  };

  const renderGatePanel = () => (
    <>
      <div className={`og__lock-wrap ${isGateOpened ? 'og__lock-wrap--open' : ''} ${isOpening ? 'og__lock-wrap--opening' : ''}`}>
        <img src={getLockImage()} alt={getStatusText()} className="og__lock-img" />
      </div>

      <div className="og__status">
        <h1 className={`og__status-title ${isGateOpened ? 'og__status-title--success' : ''}`}>
          {getStatusText()}
        </h1>
        <p className="og__status-sub">{getStatusSub()}</p>
      </div>

      {!isGateOpened && (
        <div className="og__slider-wrap">
          <SSlider
            onUnlock={handleUnlock}
            isUnlocked={isUnlocked}
            isOpening={isOpening}
            isGateOpened={isGateOpened}
          />
        </div>
      )}

      {isGateOpened && (
        <div className="og__success-badge">
          <span className="og__success-icon">✓</span>
        </div>
      )}
    </>
  );

  const renderFacilityPanel = () => (
    <>
      <div className={`og__lock-wrap ${isFacilityOpened ? 'og__lock-wrap--open' : ''} ${isFacilityOpening ? 'og__lock-wrap--opening' : ''}`}>
        <img src={getFacilityLockImage()} alt={getFacilityStatusText()} className="og__lock-img" />
      </div>

      <div className="og__status">
        <h1 className={`og__status-title ${isFacilityOpened ? 'og__status-title--success' : ''}`}>
          {getFacilityStatusText()}
        </h1>
        <p className="og__status-sub">{getFacilityStatusSub()}</p>
      </div>

      {!isFacilityOpened && (
        <div className="og__slider-wrap">
          <SSlider
            onUnlock={handleFacilityUnlock}
            isUnlocked={isFacilityUnlocked}
            isOpening={isFacilityOpening}
            isGateOpened={isFacilityOpened}
          />
        </div>
      )}

      {isFacilityOpened && (
        <div className="og__success-badge">
          <span className="og__success-icon">✓</span>
        </div>
      )}
    </>
  );

  return (
    <div className="og">
      <div className="og__header">
        <SBack onClick={() => navigate('/home')} />
      </div>

      <div className={`og__body ${showFacilitySlider ? 'og__body--tabbed' : ''}`}>
        {isCheckingAccessControl ? (
          <div className="og__state">
            <SLoader />
          </div>
        ) : showFacilitySlider ? (
          <>
            <p className="og__tabs-hint">Select a door to unlock</p>
            <div className="og__tabs og__tabs--in" role="tablist">
              <div
                className="og__tabs-pill"
                style={{ transform: activeTab === 'gate' ? 'translateX(0%)' : 'translateX(100%)' }}
              />
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'gate'}
                className={`og__tab ${activeTab === 'gate' ? 'og__tab--active' : ''}`}
                onClick={() => setActiveTab('gate')}
              >
                Door
                {isGateOpened && <span className="og__tab-check">✓</span>}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'facility'}
                className={`og__tab ${activeTab === 'facility' ? 'og__tab--active' : ''}`}
                onClick={() => setActiveTab('facility')}
              >
                {facilityLabel}
                {isFacilityOpened && <span className="og__tab-check">✓</span>}
              </button>
            </div>

            <div key={activeTab} className="og__door og__door--animated">
              {activeTab === 'gate' ? renderGatePanel() : renderFacilityPanel()}
            </div>
          </>
        ) : (
          <div className="og__door og__door--animated">
            {renderGatePanel()}
          </div>
        )}
      </div>

      <div className="og__footer">
        <p className="og__contact-label">Experiencing issues?</p>
        <a href={`tel:${SUPPORT_PHONE}`} className="og__contact-link">
          {SUPPORT_DISPLAY}
        </a>
      </div>
    </div>
  );
};

export default OpenGate;
